import React from 'react';
import { useField, ErrorMessage } from 'formik';
import classNames from 'classnames';

interface FormTextFieldProps {
    fieldName: string;
    label: string;
    readOnly?: boolean;
    required?: boolean;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    tooltipIconData?: {
        mainIcon: string;
        closeIcon: string;
    };
    tooltip?: string | null;
    renderTooltipTextAsHtml?: boolean;
}

// Tooltip component
const FormFieldTooltip = ({
    tooltipText,
    tooltipMainIconSource,
    tooltipCloseIconSource,
    renderTooltipTextAsHtml,
}: {
    tooltipText: string;
    tooltipMainIconSource: string;
    tooltipCloseIconSource: string;
    renderTooltipTextAsHtml?: boolean;
}) => {
    const [showTooltipText, setShowTooltipText] = React.useState(false);

    return (
        <div className="FormTooltipContainer">
            <div
                className="TooltipIconContainer"
                onClick={() => setShowTooltipText(!showTooltipText)}
            >
                <img
                    src={tooltipMainIconSource}
                    alt="Tooltip clickable icon"
                    className="TooltipMainIcon"
                />
            </div>
            {showTooltipText && (
                <div className="TooltipTextContainer">
                    <img
                        src={tooltipCloseIconSource}
                        alt="Close tooltip"
                        className="TooltipCloseIcon"
                        onClick={() => setShowTooltipText(false)}
                    />
                    {renderTooltipTextAsHtml ? (
                        <div className="Text" dangerouslySetInnerHTML={{ __html: tooltipText }} />
                    ) : (
                        <div className="Text">{tooltipText}</div>
                    )}
                </div>
            )}
        </div>
    );
};

// Main FormTextField component
const FormTextField: React.FC<FormTextFieldProps> = ({
    fieldName,
    label,
    readOnly = false,
    required = false,
    inputProps,
    tooltipIconData,
    tooltip,
    renderTooltipTextAsHtml = false,
}) => {
    const [field, meta] = useField(fieldName);
    const hasError = meta.touched && meta.error;

    return (
        <div
            className={classNames('FormBox FormSideBySide', {
                FormFieldRequired: required,
            })}
        >
            <div className="FormLabel">
                <label htmlFor={fieldName}>{label}</label>
                {tooltip && tooltipIconData && (
                    <FormFieldTooltip
                        tooltipText={tooltip}
                        tooltipMainIconSource={tooltipIconData.mainIcon}
                        tooltipCloseIconSource={tooltipIconData.closeIcon}
                        renderTooltipTextAsHtml={renderTooltipTextAsHtml}
                    />
                )}
            </div>
            <div className="FormField">
                <input
                    {...field}
                    {...inputProps}
                    id={fieldName}
                    name={fieldName}
                    className={classNames('EditBox', {
                        ViewOnly: readOnly,
                        ErrorInput: hasError,
                    })}
                    readOnly={readOnly}
                />
                <ErrorMessage name={fieldName}>
                    {(msg) => <div className="ErrorLabel">{msg}</div>}
                </ErrorMessage>
            </div>
        </div>
    );
};

export default FormTextField;
